import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import * as RadixToast from '@radix-ui/react-toast';
import './Toast.css';

export type ToastTone = 'neutral' | 'success' | 'danger' | 'warning';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds; danger toasts default to sticking around longer. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastApi {
  toast: (opts: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/** Wraps the app once; `useToast().toast(...)` anywhere below. Radix handles focus and swipe. */
export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback((opts: ToastOptions) => {
    setItems((prev) => [...prev, { ...opts, id: Date.now() + Math.random() }]);
  }, []);
  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const api = useMemo(() => ({ toast }), [toast]);
  return (
    <ToastContext.Provider value={api}>
      <RadixToast.Provider swipeDirection="right" label="Notifications">
        {children}
        {items.map((t) => (
          <ToastView key={t.id} item={t} onClose={() => remove(t.id)} />
        ))}
        <RadixToast.Viewport className="pui-toast-viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onClose }: { item: ToastItem; onClose: () => void }): ReactNode {
  const tone = item.tone ?? 'neutral';
  const duration = item.duration ?? (tone === 'danger' ? 8000 : 4000);
  return (
    <RadixToast.Root
      className={`pui-toast pui-toast--${tone}`}
      duration={duration}
      onOpenChange={(open) => !open && onClose()}
    >
      <div className="pui-toast__body">
        <RadixToast.Title className="pui-toast__title">{item.title}</RadixToast.Title>
        {item.description ? (
          <RadixToast.Description className="pui-toast__desc">
            {item.description}
          </RadixToast.Description>
        ) : null}
      </div>
      <RadixToast.Close className="pui-toast__close" aria-label="Dismiss">
        {'×'}
      </RadixToast.Close>
    </RadixToast.Root>
  );
}
